import { useQuery } from "@tanstack/react-query";
import { type IntakeField, useTicketIntakeApi } from "@/api/ticket-intake";
import { Button } from "@/components/ui/button";
import { useIntakeMessages } from "./intake-messages";

/** Render the immutable schema and labels captured at creation, including retired fields. */
export function TicketIntakeSubmission({ ticketId }: { ticketId: string }) {
  const api = useTicketIntakeApi();
  const m = useIntakeMessages();
  const query = useQuery({ queryKey: ["ticket-intake", "submission", ticketId], queryFn: ({ signal }) => api.getSubmission(ticketId, signal) });
  if (query.isPending) return <p className="p-4 text-sm text-muted-foreground">{m.loading}</p>;
  if (query.isError)
    return (
      <div role="alert" className="space-y-2 p-4 text-sm">
        <p>{m.error}</p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          {m.retry}
        </Button>
      </div>
    );
  if (!query.data) return <p className="p-4 text-sm text-muted-foreground">{m.none}</p>;
  const submission = query.data;
  return (
    <section className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {submission.form.name}
          {submission.form.version ? ` v${submission.form.version}` : ""}
        </h3>
        <time className="text-xs text-muted-foreground" dateTime={submission.creationTime}>
          {new Date(submission.creationTime).toLocaleString()}
        </time>
      </div>
      <dl className="divide-y rounded-lg border px-4">
        {submission.form.schema.fields
          .filter((field) => Object.hasOwn(submission.fieldValues, field.key))
          .map((field) => (
            <div className="grid gap-1 py-3 sm:grid-cols-3 sm:gap-4" key={field.key}>
              <dt className="text-sm text-muted-foreground">{field.label}</dt>
              <dd className="break-words whitespace-pre-wrap text-sm sm:col-span-2">
                {formatIntakeValue(field, submission.displayValues[field.key] ?? submission.fieldValues[field.key], m.yes, m.no)}
              </dd>
            </div>
          ))}
      </dl>
    </section>
  );
}

export function formatIntakeValue(field: IntakeField, value: unknown, yes: string, no: string): string {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? yes : no;
  if (Array.isArray(value)) return value.map((item) => formatIntakeValue(field, item, yes, no)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return field.options?.find((option) => option.value === String(value))?.label ?? String(value);
}
